//REACT
import { useEffect, useState, useRef, useMemo } from "react";
// GRAPHQL
import { useQuery } from "@apollo/client";
import { canBulkLaunch, canViewAllReports, GET_STUDENTS_PAGE, getFragmentData, LocationDataType, LocationModel, SCREEN_VERSION, SEND_SESSION_REQUEST_ACTION, Student, StudentCustomField } from "ft-shared-graphql";

// MUI AND DIV COMPONENTS
import { GridColDef, GridNoRowsOverlay, GridRowId, GridRowModel, GridRenderCellParams } from "@mui/x-data-grid";
import { IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
// import type { ChildrenFilterOption, StandardFilterOption } from "ui"
import { StandardFilterOption, TableToolBarStacked, TablePaginated, FetchMoreCallbackArgs, StudentCustomCheckbox, HeaderActionType, } from "ui";
// import { CustomCheckBoxColumn, ParentalConsentCell } from "./Cells";
import { Close, OpenInNew, Description, FolderShared, AddBox, Launch } from "@mui/icons-material";

// QUERIES AND MUTATIONS
// CONTEXT AND HOOKS
import { useSlideInContext } from "ui/SlideIn";
import {
    ADD_STUDENT,
    GET_TRIAGE, BULK_LAUNCH
} from "../../SlideIn";
import { DateCell } from "ui";
import { differenceInYears, parseISO } from "date-fns";
import { dateOnlyISO8601 } from "ft-shared-graphql/tools"
import { QueryOrigin, SELECTED_STUDENT_FRAGMENT, PersonModel } from "ft-shared-graphql";
import ParentalConsentCell from "./Cells/ParentalConsentCell";
import { GetTriageRequest } from "../../SlideIn/GetTriage/GetTriage";
import { ChildrenFilterOption } from "ui/Tables/TableFilter";

const createCustomField = (customField: any): GridColDef => {
    return {
        field: customField.name ?? "missing",
        headerName: customField.name ?? "missing",
        flex: 1,
        sortable: true,
        valueGetter: (value, row) => {
            const studentCustomField = row.studentCustomFields.find((x: StudentCustomField) => x.customField?.id === customField.id)
            return studentCustomField ? studentCustomField.value : null
        },
        renderCell: (params: GridRenderCellParams) => {
            const studentCustomField = params.row.studentCustomFields.find(
                (x: StudentCustomField) => x.customField?.id === customField.id
            ) as StudentCustomField;

            return studentCustomField ? <>{studentCustomField.value}</> : null;
        },
    };
};

const unique = <T,>(array: T[]): T[] => {
    return Array.from(new Set(array));
}

const mapEdgesToTable = (data: any) => {
    return {
        ...data.node,
        age: differenceInYears(new Date(), parseISO(dateOnlyISO8601(data.node.dob)))
    };
};

function deltaSelections(a: GridRowId[], b: GridRowId[], c: GridRowId[]): GridRowId[] {
    const added = a.filter(x => !b.includes(x));
    const removed = b.filter(x => !a.includes(x));

    return ([] as GridRowId[])
        .concat(c.filter(x => !removed.includes(x)))
        .concat(added);
}

const DEFAULT_PAGE_SIZE = 100;

const TABLE_COLUMNS: GridColDef[] = [
    { field: "firstName", headerName: "First Name", flex: 1, sortable: true },
    { field: "lastName", headerName: "Last Name", flex: 1, sortable: true },
    {
        field: "dob", headerName: "Date of Birth", flex: 1, sortable: true,
        renderCell: ({ value }) => {
            return value ? <DateCell value={value.toString()} type={"age"} /> : ""
        }
    },
];

type SelectedAction = "LAUNCH" | "REPORTS" | "DETAILS" | "EXPIRE"

type StudentTableProps = {
    title: string,
    showNew: boolean
    bulkStudents: { allStudents: Student[], crashedStudents: Student[], successfulStudents: Student[] };
    updateBulkStudents: (assign: Student[], unassign: Student[], crash: Student[], success: Student[]) => void;
    onBack?: () => void,
    onSelectedStudent: (record: any, action: SelectedAction) => void,
    onBulkLaunch: (action: SEND_SESSION_REQUEST_ACTION, screenType: SCREEN_VERSION) => void,
    submitTriage: (values: GetTriageRequest) => void,
    person: PersonModel,
    roles: string[]

};

export const StudentTable = ({ onBack, onSelectedStudent, title, showNew, person, roles, bulkStudents, updateBulkStudents, onBulkLaunch, submitTriage }: StudentTableProps) => {
    const { handleContent } = useSlideInContext();
    const [pageLoading, setPageLoading] = useState<boolean>(false);
    const defaultLocation = person.company.defaultLocation
    const companyConsentPolicy = person.company.consentPolicy
    // const { updateBulkStudents, bulkStudents } = useStudentContext();
    const queryOrigin = window.location.pathname.includes("dashboard") ? QueryOrigin.Dashboard : QueryOrigin.Manage
    const { loading, data, fetchMore, refetch } = useQuery(GET_STUDENTS_PAGE, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "network-only",
        variables: {
            query: "",
            first: DEFAULT_PAGE_SIZE,
            origin: queryOrigin
        },
    });

    // const rows = useMemo(() => data?.students?.edges?.map(mapEdgesToTable) ?? [], [data])
    const [pageTableState, setPageTableState] = useState<{
        columns: GridColDef[];
        rows: GridRowModel[];
        filteredRows: GridRowModel[];
        selectionModel: (string | number)[];
        // selectionModel: GridRowSelectionModel;
        selections: {
            initial: GridRowId[],
            pageAssign: GridRowId[],
            prevAssign: GridRowId[],
            nextAssign: GridRowId[],
            unassign: GridRowId[],
            allSelected: GridRowId[]
        };
        status: "NORMAL" | "FILTERING";
    }>({
        columns: [],
        rows: [],
        filteredRows: [],
        selectionModel: [],
        // selectionModel: { type: "include", ids: new Set() },
        selections: {
            initial: [],
            pageAssign: [], // Contains student IDs that have a relationship with person
            prevAssign: [], // Contains student IDs across page/table
            nextAssign: [], // Contains student IDs used to created a person/student relationship
            unassign: [],   // Contains student IDs used to remove the person/student relationship
            allSelected: [] // Contains student IDs for selections on table/page
        },
        status: "NORMAL",
    });
    const [pageEdges, setPageEdges] = useState<any[] | null>(null);

    const rows = useMemo(() => {
        const edges = pageEdges ?? data?.students?.edges ?? [];
        return edges.map(mapEdgesToTable);
    }, [pageEdges, data]);

    // keep existing filter behavior

    useEffect(() => {
        if (bulkStudents.crashedStudents.length > 0) {
            const _crashedIds = bulkStudents.crashedStudents.map((student) => student?.id)
            updateBulkStudents([], [], [], [])
            handleSelectionModelChange(_crashedIds)
            // NOTE: WE WANT TO CLEAR THIS SO WE CAN CONTINUE ON?
        }
        if (bulkStudents.successfulStudents.length > 0 && bulkStudents.crashedStudents.length < 1) {
            handleSelectionModelChange([])
            updateBulkStudents([], [], [], [])

        }
    }, [bulkStudents.crashedStudents, bulkStudents.successfulStudents, pageTableState.selectionModel]) //eslint-disable-line react-hooks/exhaustive-deps
    const rowRef = useRef(pageTableState.rows)
    rowRef.current = pageTableState.rows

    // const handleFilter = (selectedOption: ChildrenFilterOption) => {
    //     setPageTableState((prevState) => {
    //         const filteredRows =
    //             selectedOption === "ACTIVE"
    //                 ? prevState.rows.filter((x) => !x.expired)
    //                 : selectedOption === "EXPIRED"
    //                     ? prevState.rows.filter((x) => x.expired)
    //                     : selectedOption === "TSCREEN_AGE"
    //                         ? prevState.rows.filter((x) => (x.age < 10))
    //                         : selectedOption === "TWEEN_AGE"
    //                             ? prevState.rows.filter((x) => (x.age > 9 && x.age < 14))
    //                             : selectedOption === "TEEN_AGE"
    //                                 ? prevState.rows.filter((x) => (x.age > 13))
    //                                 : selectedOption === "CONSENT_GIVEN"
    //                                     ? prevState.rows.filter((x) => x.parentalConsentStatus == "Given")
    //                                     : selectedOption === "CONSENT_BY_DEFAULT"
    //                                         ? prevState.rows.filter((x) => x.parentalConsentStatus == "Timelapsed")
    //                                         : selectedOption === "CONSENT_PENDING"
    //                                             ? prevState.rows.filter((x) => x.parentalConsentStatus == "Pending" || x.parentalConsentStatus == null)
    //                                             : selectedOption === "CONSENT_REJECTED"
    //                                                 ? prevState.rows.filter((x) => x.parentalConsentStatus == "Rejected")
    //                                                 :
    //                                                 prevState.rows; // Default "ALL"

    //         return {
    //             ...prevState,
    //             status: selectedOption === "ALL" ? "NORMAL" : "FILTERING",
    //             filteredRows,
    //         };
    //     });
    // };
    const [filterOption, setFilterOption] = useState<ChildrenFilterOption>("ACTIVE");

    const handleFilter = (selectedOption: ChildrenFilterOption) => {
        setFilterOption(selectedOption);
        setPageTableState((prevState) => { return { ...prevState, status: selectedOption === "ALL" ? "NORMAL" : "FILTERING" } });
    };

    const displayRows = useMemo(() => {
        switch (filterOption) {
            case "ACTIVE":
                return rows.filter((x: any) => !x.expired);
            case "EXPIRED":
                return rows.filter((x: any) => x.expired);
            case "TSCREEN_AGE":
                return rows.filter((x: any) => x.age < 10);
            case "TWEEN_AGE":
                return rows.filter((x: any) => x.age > 9 && x.age < 14);
            case "TEEN_AGE":
                return rows.filter((x: any) => x.age > 13);
            case "CONSENT_GIVEN":
                return rows.filter((x: any) => x.parentalConsentStatus == "Given");
            case "CONSENT_BY_DEFAULT":
                return rows.filter((x: any) => x.parentalConsentStatus == "Timelapsed");
            case "CONSENT_PENDING":
                return rows.filter((x: any) => x.parentalConsentStatus == "Pending" || x.parentalConsentStatus == null);
            case "CONSENT_REJECTED":
                return rows.filter((x: any) => x.parentalConsentStatus == "Rejected");
            default: // "ALL"
                return rows;
        }
    }, [rows, filterOption]);
    const isRowSelectable = (student: any): boolean => {
        const status = !student.expired //if they are expired it returns true
        const isEmailPresent = Boolean(student.email)
        const maxReached = pageTableState.selectionModel.length < 31
        const isSelectabled = status && isEmailPresent && maxReached
        return isSelectabled
    }
    const handleSelectAll = (selectAll: boolean) => {
        if (selectAll) {
            const selectableRows = pageTableState.rows.filter((row) => isRowSelectable(row))
            // NOTE: WE ARE SELECTING AND CAPPING THIS A 30 PER CHANCE 06-30-2025
            const allIds = selectableRows?.map((row) => row.id as GridRowId).slice(0, 30);
            handleSelectionModelChange(allIds)
        } else {
            handleSelectionModelChange([])
        }
    }
    const handleSelectionModelChange = (values: (string | number)[]) => {
        const selectedIds = Array.from(values)
        const prevSelectedIds = Array.from(pageTableState.selectionModel);
        const assign = selectedIds.filter(x => !prevSelectedIds.includes(x));
        const unassign = prevSelectedIds.filter(x => !selectedIds.includes(x));
        const uniqAssign = assign.filter(x => !pageTableState.selections.prevAssign.includes(x));
        setPageTableState(prevState => {
            const nextAssign = unique(deltaSelections(uniqAssign, unassign, prevState.selections.nextAssign));
            const nextUnassign = deltaSelections(unassign, assign, prevState.selections.unassign);
            const selectionModel = {
                ...prevState,
                selectionModel: values,
                selections: {
                    ...prevState.selections,
                    nextAssign,
                    unassign: nextUnassign,
                    allSelected: unique(selectedIds.concat(nextAssign).filter(x => x !== unassign[0]))
                }
            }
            return selectionModel;
        })
        const _assign = rowRef.current.filter(
            row => uniqAssign.includes(row.id)
        ) as Student[];
        const _unAssign = rowRef.current.filter(
            row => unassign.includes(row.id)
        ) as Student[];

        // const _uniqAssign = pageTableState.rows.filter(row => values.includes(row.id)) as Student[];
        // const _unAssign = pageTableState.rows.filter(row => unassign.includes(row.id)) as Student[];
        updateBulkStudents(_assign, _unAssign, [], [])
    }
    const handleOnNew = () => {
        handleContent(ADD_STUDENT, {
            defaultLocation: defaultLocation as LocationDataType,
            parentEmailNeeded: companyConsentPolicy,
            roles: roles,
            onSubmit: () => { setPageEdges(null); 
                refetch() 
            }
        })
    }

    const handleTriage = () => {
        handleContent(GET_TRIAGE, {
            defaultLocation: defaultLocation as LocationModel,
            onSubmit: submitTriage
        })
    }

    const handleBulkLaunch = () => {
        // TODO: NEED A WAY TO OFF LOAD THESE BUULK STUDENTS AFTER
        handleContent(BULK_LAUNCH, {
            updateBulkStudents: updateBulkStudents,
            bulkStudents: bulkStudents,
            onSubmit: onBulkLaunch
        })
    }
    const handleFetchMore = async (variables: FetchMoreCallbackArgs) => {
        try {
            const res = await fetchMore({
                variables: {
                    query: variables.query ?? "",
                    after: variables.after ?? null,
                    first: variables.first ?? DEFAULT_PAGE_SIZE,
                    origin: queryOrigin,
                },
            });
            setPageEdges(res?.data?.students?.edges ?? []);
        } catch (err) {
            console.log("there is an error: ", err);
        }
        // await fetchMore({
        //     variables: {
        //         query: variables.query ?? "",
        //         after: variables.after ?? null,
        //         first: variables.first ?? DEFAULT_PAGE_SIZE,
        //         origin: queryOrigin
        //     }
        // }).then((res) => {
        //     const rows = res?.data?.students?.edges?.map(mapEdgesToTable) ?? [];
        //     if (pageTableState.status === "FILTERING") {
        //         //if its filtering we don't want to mess with this, but we also want to call new things if it updates
        //         setPageTableState((prevState) => ({
        //             ...prevState,
        //             rows,
        //         }));
        //     } else {
        //         setPageTableState((prevState) => ({
        //             ...prevState,
        //             filteredRows: [],
        //             rows,
        //         }));
        //     }
        // }).catch(err => {
        //     console.log("there is an error: ", err)
        // })
    }
    const totalRows =
        pageTableState.status === "FILTERING"
            ? displayRows.length
            : data?.students?.totalCount ?? 0;
    const columns: GridColDef[] = useMemo(() => {
        const cols = [...TABLE_COLUMNS];
        data?.location?.customFields.forEach((field: any) => {
            cols.push(createCustomField(field));
        });
        if (canBulkLaunch(roles)) {
            cols.unshift(
                StudentCustomCheckbox({
                    title: "Mass Launch",
                    dataTour: "mass-launch",
                    selectionModel: pageTableState.selectionModel,
                    handleSelectionModelChange: handleSelectionModelChange,
                    handleSelectAll: handleSelectAll,
                    companyConsentPolicy: companyConsentPolicy,
                    totalRowCount: data?.students?.edges?.filter((edge: any) => isRowSelectable(edge.node)).length ?? 0
                })
            )
        }
        if (companyConsentPolicy) {
            cols.push({
                field: "parentalConsent",
                headerName: "Parental Consent",
                headerAlign: "center",
                align: "center",
                sortable: false,
                width: 75,
                renderHeader: () => (
                    <div data-tour="parental-consent" style={{ whiteSpace: "normal", textAlign: "center" }}>
                        Parental Consent
                    </div>
                ),
                renderCell: (params) => {
                    return (
                        <ParentalConsentCell parentalConsentStatus={params.row.parentalConsentStatus}
                            lastSent={params.row?.lastConsentAction?.createdAt}
                        />
                    )
                }
            })
        }
        if (!roles.includes("SUPPORT ADMIN")) {
            cols.push({
                field: "launch",
                headerName: "Launch",
                headerAlign: "center",
                align: "center",
                sortable: false,
                width: 75,
                renderHeader: () => (
                    <div data-tour="single-launch">
                        Launch
                    </div>
                ),
                renderCell: (params) => {
                    const record = data?.students?.edges?.find((edge: any) => {
                        const fragment = getFragmentData(SELECTED_STUDENT_FRAGMENT, edge.node);
                        return fragment.id === params.row.id
                    })
                    const studentStatus = params.row.expired
                    return (
                        <Tooltip title={studentStatus ? "Student is Expired" : ""}>
                            <div>
                                <IconButton onClick={() => onSelectedStudent(record?.node as Student, "LAUNCH")}>
                                    <OpenInNew />
                                </IconButton>
                            </div>
                        </Tooltip>
                    );
                },
            });
        }
        if (canViewAllReports(roles)) {
            cols.push({
                field: "reports",
                headerName: "Reports",
                headerAlign: "center",
                align: "center",
                sortable: false,
                width: 75,
                renderHeader: () => (<div data-tour="report-view">Reports</div>),
                renderCell: (params) => {
                    const record = data?.students?.edges?.find((edge: any) => {
                        const fragment = getFragmentData(SELECTED_STUDENT_FRAGMENT, edge.node);
                        return fragment.id === params.row.id
                    })

                    return (
                        <IconButton onClick={() => onSelectedStudent(record?.node as Student, "REPORTS")}>
                            <Description />
                        </IconButton>
                    );
                },
            });
        }

        cols.push({
            field: "profile",
            headerName: "Profile",
            headerAlign: "center",
            align: "center",
            sortable: false,
            width: 75,
            renderHeader: () => (<div data-tour="student-profile">Profile</div>),
            renderCell: (params) => {
                const record = data?.students?.edges?.find((edge: any) => {
                    const fragment = getFragmentData(SELECTED_STUDENT_FRAGMENT, edge.node);
                    return fragment.id === params.row.id
                })

                return (
                    <IconButton onClick={() => onSelectedStudent(record?.node as Student, "DETAILS")}>
                        <FolderShared />
                    </IconButton>
                );
            },
        });
        // if (showNew && !roles.includes("SUPPORT ADMIN")) {
        //     columns.push({
        //         field: "remove",
        //         headerName: "Remove",
        //         headerAlign: "center",
        //         align: "center",
        //         sortable: false,
        //         width: 100,
        //         renderCell: (params) => {
        //             const record = data?.students?.edges?.find((edge: any) => {
        //                 const fragment = getFragmentData(SELECTED_STUDENT_FRAGMENT, edge.node);
        //                 return fragment.id === params.row.id
        //             })
        //             return (
        //                 <Tooltip title={params.row.expired ? "This student is already expired" : ""}>
        //                     <div>
        //                         <IconButton disabled={params.row.expired} onClick={() => onSelectedStudent(record?.node as StudentDataType, "EXPIRE")}>
        //                             <Close
        //                                 style={{
        //                                     backgroundColor: params.row.expired ? "#EEE" : "#8E1C3B",
        //                                     borderRadius: "100%",
        //                                     color: params.row.expired ? "#999" : "#FFF",
        //                                 }}
        //                             />
        //                         </IconButton>
        //                     </div>
        //                 </Tooltip>
        //             );
        //         },
        //     });
        // }
        return cols;
    }, [data, roles, companyConsentPolicy, rows, pageTableState.selectionModel, handleSelectionModelChange, handleSelectAll, onSelectedStudent])

    const headerActions: HeaderActionType[] = [
        {
            name: "Add new child",
            icon: <AddBox />,
            action: handleOnNew,
            dataTour: "add-student",
            isHidden: showNew,
        }, {
            name: "Get Triage Report",
            icon: <Launch />,
            dataTour: "student-triage",
            action: handleTriage
        }, {
            name: "Bulk Launch", icon: <Launch />,
            action: handleBulkLaunch,
            isHidden: pageTableState.selectionModel.length == 0
        }
    ]
    return (
        <Paper>
            <TablePaginated
                loading={loading || pageLoading}
                renderToolbar={({ searchQuery, onSearch, onSearchClear }) => (
                    <TableToolBarStacked
                        title={`${title}`}
                        subTitle={`@${defaultLocation?.name}`}
                        headerActions={headerActions}
                        searchQuery={searchQuery ?? ""}
                        onBack={onBack}
                        onFilter={handleFilter}
                        filterTableName='children'
                        onSearch={onSearch}
                        onSearchClear={onSearchClear}
                    />
                )}
                slots={{
                    noRowsOverlay:
                        (data?.location?.totalStudents ?? 0) <= 0
                            ? NoStudentsFoundOverlay :
                            roles.includes("SUPPORT ADMIN") ? RoleBasedOverlay
                                : GridNoRowsOverlay
                }}

                pageInfo={data?.students?.pageInfo}
                pageSize={DEFAULT_PAGE_SIZE}
                columns={columns}
                // columns={pageTableState.columns}
                // checkboxSelection={true}
                // selectionProps={{
                //     rowSelectionModel: pageTableState.rows.length > 0 ? { type: 'include', ids: new Set(pageTableState.selections.allSelected) } : { type: 'include', ids: new Set() },
                //     onRowSelectionModelChange: handleSelectionModelChange
                // }}
                // rows={
                //     pageTableState.status === "FILTERING"
                //         ? pageTableState.filteredRows
                //         : pageTableState.rows
                // }
                rows={displayRows}
                getRowClassName={(params) => params.row.expired ? "Expired" : "Active"}
                totalRows={totalRows}
                onFetchMore={handleFetchMore}
            />
        </Paper>
    );
};

// function delay(ms: number) {
//     return new Promise((resolve) => setTimeout(resolve, ms));
// }

function NoStudentsFoundOverlay() {
    // const roles = usePersonAuthStore(state => state.roles)
    const _location = window.location.pathname.includes("dashboard")
    if (_location) {
        return (<Stack height="100%" alignItems="center" justifyContent="center">
            <Typography width={500}>
                You do not have any children directly associated with your account
                To get started go to click "Add new Child" or contact your futuresThrive representative to facilitate a bulk upload.
            </Typography>
        </Stack>)
    }
    // else if (roles.includes("SUPPORT ADMIN")) {
    //     return (<Stack height="100%" alignItems="center" justifyContent="center">
    //         <Typography width={500}>
    //             There are no children at this location. Please contact your admin to add new children or contact your futuresThrive representative to facilitate a bulk upload
    //         </Typography>
    //     </Stack>)
    // } 
    else
        return (
            <Stack height="100%" alignItems="center" justifyContent="center">
                <Typography width={500}>
                    You do not have any children associated with this location.
                    Select “Add New Child” to get started or contact your futuresThrive representative to facilitate a bulk upload.
                </Typography>
            </Stack>
        )
}

function RoleBasedOverlay() {
    const _location = window.location.pathname.includes("dashboard")
    if (_location) {
        return (<Stack height="100%" alignItems="center" justifyContent="center">
            <Typography width={500}>
                You do not have any children directly associated with your account based on your assigned role.
                To view children at your location(s) please go to "Manage Children" in the menu
            </Typography>
        </Stack>)
    } else {
        return (<Stack height="100%" alignItems="center" justifyContent="center">
            <Typography width={500}>
                There are no children at this location. Please contact your admin to add new children or contact your futuresThrive representative to facilitate a bulk upload
            </Typography>
        </Stack>)
    }
}
